import * as THREE from 'three';
import { WORLD_PER_HEX } from '../coords.ts';
import type { UnRender } from './ctx.ts';

/**
 * Vela's own effects: her shots of smoke and cold light through the heroes, the smoke arrow's long line, the smoke
 * clinging to the heroes it marked, her leap and the black roots she leaves, and the Endless rain falling in its
 * circles. Only vfx primitives and ground glows: nothing is made mid-fight.
 */
const K = WORLD_PER_HEX / 1000;
const TAU = Math.PI * 2;
const GLOW = '#7fa8ff';
const ACCENT = '#5f8cff';
const PALE = '#cfe0ff';
const SMOKE = '#1a1e2c';
const ROOT = new THREE.Color('#3d5fbf');
const RAIN = new THREE.Color(ACCENT);
const SMOKED = new THREE.Color('#6f7f9f');

export const velaFx: UnRender = {
  look: { glow: GLOW, accent: ACCENT, pale: PALE, dark: '#0f1220', smoke: SMOKE },
  clips: { velaArrow: 'arrow', velaLeap: 'leap' },
  frame: (c, s, e, p, yaw, h) => {
    const low = c.low;
    const u = e.un;
    if (!u) return;
    // Mid-leap: a wake of smoke behind her.
    if (u.busy > 0) c.vfx.burst(p.clone().setY(p.y + h * 0.4), { n: low ? 1 : 3, color: SMOKE, speed: 0.02, up: 0.02, size: 0.5, life: 14, smoke: true, spread: 0.2 });
    // The empty string glows a moment as she looses.
    if (e.anim === 'attack' && e.animT === 1 && u.cast === 0) {
      const fwd = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
      c.flare(p.clone().addScaledVector(fwd, 0.4).setY(p.y + h * 0.6), GLOW, 0.8, 4);
    }
    // Her ground: the roots waiting, the rain falling.
    for (const z of s.zones) {
      if (z.kind === 'velaRoots') {
        const q = c.at(z.x, z.y, 0.05);
        const r = z.radius * K;
        const armed = (z.warn ?? 0) <= 0;
        c.glow(q, ROOT, r * 2.2, armed ? 0.4 + 0.15 * Math.sin(s.tick * 0.7 + z.id) : 0.2);
        if ((s.tick + z.id) % 6 === 0) c.vfx.ring(q, r * 0.3, r, armed ? ACCENT : SMOKE, 10, 0.55);
        if ((s.tick + z.id) % 4 === 0) {
          const a = ((s.tick * 7 + z.id * 13) % 32) / 32 * TAU;
          const at = q.clone().add(new THREE.Vector3(Math.cos(a) * r * 0.6, 0.1, Math.sin(a) * r * 0.6));
          c.vfx.burst(at, { n: low ? 1 : 2, color: '#0b0d16', speed: 0.01, up: 0.03, size: 0.35, life: 14, smoke: true, spread: 0.15 });
          c.vfx.burst(at, { n: 1, color: GLOW, speed: 0.01, up: 0.04, size: 0.06, life: 10, gravity: -0.002 });
        }
      } else if (z.kind === 'velaRain') {
        const q = c.at(z.x, z.y, 0.06);
        const r = z.radius * K;
        c.glow(q, RAIN, r * 2, 0.32);
        if ((s.tick + z.id) % 3 === 0) c.vfx.ring(q, r * 0.97, r, PALE, 4, 0.7);
        // Arrows of smoke streaking down at random points of the circle.
        const n = low ? 3 : 7;
        for (let i = 0; i < n; i++) {
          const a = Math.random() * TAU;
          const d = Math.sqrt(Math.random()) * r;
          const g = q.clone().add(new THREE.Vector3(Math.cos(a) * d, 0, Math.sin(a) * d));
          const top = g.clone().add(new THREE.Vector3(-0.25, 3.2, -0.15));
          c.vfx.beam(top, g, i % 2 ? PALE : GLOW, 2, 0.025, 0);
          if (i % 2 === 0) c.vfx.burst(g, { n: 1, color: SMOKE, speed: 0.02, up: 0.02, size: 0.35, life: 10, smoke: true, spread: 0.1 });
        }
      }
    }
    // Smoke clinging to the heroes her arrow marked.
    for (const o of s.heroes) {
      if (!((o.unSmoke ?? 0) > 0) || o.downTicks > 0) continue;
      const q = c.at(o.x, o.y, 0.9);
      c.glow(q.clone().setY(q.y - 0.85), SMOKED, 1.2, 0.3);
      if ((s.tick + o.id) % 3 === 0) c.vfx.burst(q, { n: low ? 1 : 2, color: '#2a3042', speed: 0.01, up: 0.025, size: 0.4, life: 14, smoke: true, spread: 0.25 });
    }
  },
  skill: (c, _s, _e, name, p, h) => {
    if (name === 'velaLeap') {
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: c.low ? 4 : 10, color: SMOKE, speed: 0.06, up: 0.04, size: 0.6, life: 18, smoke: true, spread: 0.3 });
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 1.1, ACCENT, 8, 0.8);
      return true;
    }
    if (name === 'velaArrow') {
      c.flare(p.clone().setY(p.y + h * 0.6), GLOW, 1.4, 8);
      return false;
    }
    return false;
  },
  beam: (c, kind, a, b) => {
    if (kind === 'velaShot') {
      // A shot of smoke and cold light off the empty string.
      c.vfx.beam(a, b, PALE, 3, 0.03, 0);
      c.vfx.beam(a, b, ACCENT, 5, 0.06, 0.05);
      c.vfx.burst(b, { n: c.low ? 2 : 4, color: '#2a3042', speed: 0.03, up: 0.02, size: 0.35, life: 12, smoke: true, spread: 0.1 });
      c.vfx.burst(b, { n: c.low ? 2 : 4, color: GLOW, speed: 0.06, up: 0.04, size: 0.06, life: 6 });
      return true;
    }
    if (kind === 'velaArrow') {
      // The smoke arrow: a long dark line through them, lit inside, smoke left hanging along it.
      c.vfx.shaft(a, b, ACCENT, 10, 0.35);
      c.vfx.beam(a, b, PALE, 6, 0.05, 0);
      const n = c.low ? 4 : 9;
      for (let i = 1; i <= n; i++) c.vfx.burst(a.clone().lerp(b, i / n), { n: 1, color: SMOKE, speed: 0.01, up: 0.02, size: 0.6, life: 22, smoke: true, spread: 0.15 });
      c.shake(0.08);
      return true;
    }
    if (kind === 'velaLeap') {
      const g1 = a.clone().setY(a.y - 0.6);
      const g2 = b.clone().setY(b.y - 0.4);
      c.vfx.beam(g1, g2, GLOW, 6, 0.03, 0.1);
      return true;
    }
    return false;
  },
  boom: (c, _s, e, kind, p, r) => {
    const low = c.low;
    if (kind === 'velaLand') {
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.1, 0.9, ACCENT, 8, 0.8);
      c.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 3 : 8, color: SMOKE, speed: 0.05, up: 0.03, size: 0.5, life: 14, smoke: true, spread: 0.3 });
      return true;
    }
    if (kind === 'velaRoots') {
      // The roots snap shut on whoever stepped on them.
      // `r` comes in world units (times K again the ring shrank to nothing).
      const R = r;
      c.vfx.ring(p.clone().setY(p.y + 0.05), R, 0.1, ACCENT, 8, 1);
      c.vfx.implode(p.clone().setY(p.y + 0.3), R, { n: low ? 6 : 14, color: '#2b3a6a', size: 0.12, life: 10 });
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 3 : 7, color: '#0b0d16', speed: 0.04, up: 0.06, size: 0.5, life: 16, smoke: true, spread: 0.3 });
      c.flare(p.clone().setY(p.y + 0.5), GLOW, 1.2, 6);
      return true;
    }
    if (kind === 'velaVolley') {
      // A volley lands: a ripple through the circle, splinters of light.
      const R = r;
      c.vfx.ring(p.clone().setY(p.y + 0.06), R * 0.2, R, ACCENT, 6, 0.5);
      c.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 4 : 10, color: PALE, speed: 0.12, up: 0.06, size: 0.06, life: 7, spread: R * 0.6 });
      c.shake(0.08);
      return true;
    }
    if (kind === 'velaRainFall') {
      // The rain loosed: she lets go at the sky, a flash on her, the first volley on its way.
      if (e) c.force(e.id, 'rain');
      c.vfx.beam(p.clone().setY(p.y + 1.6), p.clone().setY(p.y + 8), PALE, 8, 0.08, 0);
      c.flare(p.clone().setY(p.y + 2), GLOW, 4, 10);
      c.shake(0.4);
      return true;
    }
    return false;
  },
};
